import { BugoData } from '@shared/api/bugo/bugo.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import {
  BulkDeliveryAddress,
  GiftOrderCreateDto,
  GiftOrderDeliveryMode,
  GiftOrderItemCreateDto,
  GiftSelectedOption,
  GiftThanksPrintDto,
} from '@shared/api/giftOrder/giftOrder.interface';

import { calcOrderSurcharge } from './resolveDeliveryFee';

interface OrderItemInput {
  giftItem: GiftItemForStore;
  quantity: number;
  selectedOptions?: GiftSelectedOption[];
}

/** 선택한 추가옵션 추가금 합계 */
const optionDeltaSum = (selectedOptions?: GiftSelectedOption[]): number =>
  (selectedOptions ?? []).reduce((sum, o) => sum + (o.priceDelta || 0), 0);

interface IParams {
  bugo: BugoData;
  receiverName: string;
  receiverPhoneNumber: string;
  items: OrderItemInput[];
  deliveryMode?: GiftOrderDeliveryMode;
  bulkDeliveryAddress?: BulkDeliveryAddress;
  thanksPrint?: GiftThanksPrintDto;
}

/**
 * 다중 상품 GiftOrder DTO 생성 (백엔드 스펙 v2)
 */
export const getGiftOrderDto = ({
  bugo,
  receiverName,
  receiverPhoneNumber,
  items,
  deliveryMode,
  bulkDeliveryAddress,
  thanksPrint,
}: IParams): GiftOrderCreateDto => {
  // 단가 = priceRetail + Σ 선택옵션 추가금
  const unitPriceOf = (item: OrderItemInput) =>
    (item.giftItem.priceRetail || 0) + optionDeltaSum(item.selectedOptions);

  const totalPrice = items.reduce(
    (sum, item) => sum + unitPriceOf(item) * item.quantity,
    0,
  );

  const taxableGoods = items.reduce((sum, item) => {
    const isTaxable = item.giftItem.giftItemInfo?.isTaxable ?? false;
    if (!isTaxable) return sum;
    return sum + unitPriceOf(item) * item.quantity;
  }, 0);

  // 소량주문 추가금 — 라인별 (개당 추가금 × 수량), 과세/면세 구분해 가산
  const surcharge = calcOrderSurcharge(
    items.map((item) => ({
      giftItemInfo: item.giftItem.giftItemInfo,
      quantity: item.quantity,
    })),
  );

  const taxableAmount = taxableGoods + surcharge.taxable;
  const finalTotal = totalPrice + surcharge.total;
  const taxFreeAmount = finalTotal - taxableAmount;
  const vatAmount = Math.round(taxableAmount / 11);

  const orderDetails: GiftOrderItemCreateDto[] = items.map((item) => {
    const isCouponType = item.giftItem.giftItemInfo?.type === GiftItemInfoType.Coupon;

    const orderDetail: GiftOrderItemCreateDto = {
      giftItem: item.giftItem._id,
      quantity: item.quantity,
    };

    if (item.selectedOptions && item.selectedOptions.length > 0) {
      orderDetail.selectedOptions = item.selectedOptions;
    }

    if (isCouponType) {
      orderDetail.couponDetail = { totalCount: item.quantity };
    }

    return orderDetail;
  });

  return {
    bugo: bugo._id,
    receiverName,
    receiverPhoneNumber,
    deliveryMode,
    bulkDeliveryAddress,
    orderDetails,
    thanksPrint,
    paymentDetail: {
      paymentByCash: finalTotal,
      taxFreeAmount,
      taxableAmount,
      vatAmount,
      paymentByPoint: 0,
    },
    webBaseUrl: process.env.REACT_APP_WEB_BASE_URL,
  };
};

/**
 * 단일 상품용 간편 DTO 생성 (바로구매용)
 */
export const getGiftOrderDtoSingle = ({
  giftItem,
  bugo,
  quantity,
  receiverName,
  receiverPhoneNumber,
  deliveryMode,
  bulkDeliveryAddress,
  selectedOptions,
  thanksPrint,
}: {
  giftItem: GiftItemForStore;
  bugo: BugoData;
  quantity: number;
  receiverName: string;
  receiverPhoneNumber: string;
  deliveryMode?: GiftOrderDeliveryMode;
  bulkDeliveryAddress?: BulkDeliveryAddress;
  selectedOptions?: GiftSelectedOption[];
  thanksPrint?: GiftThanksPrintDto;
}): GiftOrderCreateDto => {
  return getGiftOrderDto({
    bugo,
    receiverName,
    receiverPhoneNumber,
    deliveryMode,
    bulkDeliveryAddress,
    thanksPrint,
    items: [{ giftItem, quantity, selectedOptions }],
  });
};
